import React from 'react';
import 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { Box } from '@mui/material';
import { TypographySettings } from '../../../components/config';
import { formatNumber } from '../../../utilities';
import { formatMillions } from '../intervention-ladder/intervention-cost';
import { externalCostColor, internalCostColor } from '../intervention-ladder/intervention-ladder-colors';

const textColor = '#5b6b73';
const darkTextColor = '#16323d';
const gridColor = 'rgba(24, 76, 97, 0.08)';
const font = TypographySettings.DefaultFontFamily;

interface TooltipItem {
  index: number;
  datasetIndex: number;
  value: string;
}

interface TooltipData {
  labels: string[];
  datasets: { label: string; stack: string }[];
}

interface DataLabelContext {
  dataIndex: number;
  dataset: { totals?: number[] };
}

const barGap = { borderColor: '#ffffff', hoverBorderColor: '#ffffff', borderWidth: { left: 2, right: 2, top: 0, bottom: 0 } };

const axisTitle = { display: true, fontSize: 12, fontStyle: 'bold', fontColor: textColor, fontFamily: font };

const options = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  layout: { padding: { top: 24 } },
  scales: {
    xAxes: [
      {
        stacked: true,
        gridLines: { display: false, drawBorder: false },
        // The padding leaves room for the years under the bars.
        ticks: { autoSkip: false, maxRotation: 0, padding: 18, fontSize: 13, fontStyle: 'bold', fontColor: darkTextColor, fontFamily: font },
        scaleLabel: { ...axisTitle, labelString: 'Paragraf' },
        // A fixed thickness puts a group's bars side by side; their white side borders make the gap.
        barThickness: 34,
      },
    ],
    yAxes: [
      {
        stacked: true,
        gridLines: { color: gridColor, zeroLineColor: gridColor, drawBorder: false, drawTicks: false },
        ticks: {
          beginAtZero: true,
          padding: 8,
          maxTicksLimit: 5,
          fontSize: 12,
          fontColor: textColor,
          fontFamily: font,
          callback: (value: number) => formatNumber(value / 1_000_000, 1),
        },
        scaleLabel: { ...axisTitle, labelString: 'Udgifter (mio. kr.)' },
      },
    ],
  },
  tooltips: {
    backgroundColor: darkTextColor,
    displayColors: false,
    xPadding: 10,
    yPadding: 8,
    titleFontFamily: font,
    bodyFontFamily: font,
    callbacks: {
      title: (items: TooltipItem[], data: TooltipData) => `${data.labels[items[0].index]} · ${data.datasets[items[0].datasetIndex].stack}`,
      label: (item: TooltipItem, data: TooltipData) => `${data.datasets[item.datasetIndex].label} ${formatMillions(Number(item.value))} mio. kr.`,
    },
  },
  plugins: {
    datalabels: {
      // Only the top of each stack carries the year's total.
      display: (context: DataLabelContext) => (context.dataset.totals?.[context.dataIndex] ?? 0) > 0,
      formatter: (_: number, context: DataLabelContext) => formatMillions(context.dataset.totals?.[context.dataIndex] ?? 0),
      anchor: 'end',
      align: 'end',
      offset: 2,
      color: darkTextColor,
      font: { size: 11, weight: 600, family: font },
    },
  },
};

// The years share labels, and the chart matches datasets by this key when the data changes.
function datasetKey(dataset: { stack: string; label: string }) {
  return `${dataset.stack} ${dataset.label}`;
}

export interface InterventionCostYearSeries {
  year: number;
  internal: number[];
  external: number[];
}

export interface InterventionCostChartProps {
  labels: string[];
  years: InterventionCostYearSeries[];
}

export function InterventionCostChart({ labels, years }: InterventionCostChartProps) {
  return (
    <Box sx={{ height: 340 }}>
      <Bar
        data={{
          labels,
          datasets: years.flatMap(year => [
            {
              label: 'Interne udgifter',
              stack: `${year.year}`,
              data: year.internal,
              backgroundColor: internalCostColor,
              hoverBackgroundColor: internalCostColor,
              ...barGap,
              datalabels: {
                display: true,
                formatter: () => `${year.year}`,
                anchor: 'start',
                align: 'bottom',
                offset: 4,
                color: textColor,
                font: { size: 10, weight: 400, family: font },
              },
            },
            {
              label: 'Eksterne udgifter',
              stack: `${year.year}`,
              data: year.external,
              backgroundColor: externalCostColor,
              hoverBackgroundColor: externalCostColor,
              ...barGap,
              totals: year.internal.map((internal, index) => internal + year.external[index]),
            },
          ]),
        }}
        options={options}
        datasetKeyProvider={datasetKey}
      />
    </Box>
  );
}
